/**
 * Вход. Одна кнопка, один код, ни одного пароля.
 *
 * Пароля нет не ради моды. Пароль — это строка, которую можно переслать в чат
 * вместе с доступом; код живёт пять минут, привязан к попросившему браузеру и
 * гасится первым же обменом. Передать доступ можно только вместе с телефоном, а
 * телефон отдают неохотно — на этом и стоит вся защита от складчины.
 *
 * Опрос статуса, а не ожидание редиректа из Telegram: человек уходит в
 * приложение и возвращается на ту же вкладку, где уже открыта лента. Возврат
 * ссылкой заставил бы браузер перезагрузить страницу и потерять сокет.
 *
 * Ожидание нарисовано плиткой, а не строчкой подсказки. Пока человек ходит в
 * Telegram, эта вкладка — единственное место, где написано, что происходит и
 * сколько это ещё действует; строчка мелким шрифтом на такой вопрос не отвечает,
 * и человек жмёт «войти» второй раз, получая второй код вместо входа.
 */

import { useEffect, useState } from 'react';

import { useSession } from './session';

/** Как часто спрашиваем, нажал ли человек «начать». */
const POLL_MS = 1500;

/** Остаток словами циферблата: 296 секунд человек не читает, «4:56» читает. */
function clock(left: number): string {
  const min = Math.floor(left / 60);
  return `${min}:${String(left - min * 60).padStart(2, '0')}`;
}

export function Login({ onDone }: { onDone?: () => void }): React.ReactElement {
  const waiting = useSession((s) => s.waiting);
  const error = useSession((s) => s.error);
  const botName = useSession((s) => s.me.botName);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (waiting === null) return;
    const ask = async (): Promise<void> => {
      if (await useSession.getState().poll()) onDone?.();
    };
    const poll = setInterval(() => void ask(), POLL_MS);
    const tick = setInterval(
      () => setLeft(Math.max(0, Math.round((waiting.until - Date.now()) / 1000))),
      1000,
    );
    setLeft(Math.max(0, Math.round((waiting.until - Date.now()) / 1000)));
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [waiting, onDone]);

  if (waiting === null) {
    return (
      <div className="nx-login">
        <button
          type="button"
          className="nx-login__cta"
          onClick={() => void useSession.getState().begin()}
        >
          Войти через Telegram
        </button>
        <p className="nx-login__hint">
          Пароля нет: бот{botName === null ? '' : ` @${botName}`} узнаёт вас сам. Он же присылает
          сигналы и подтверждает новые устройства.
        </p>
        {error !== null && <p className="nx-login__err">{error}</p>}
      </div>
    );
  }

  return (
    // Живая область: экранный диктор обязан прочитать появившийся код, а не
    // молчать о том, что кнопка сменилась ожиданием.
    <div className="nx-login nx-login--wait" aria-live="polite">
      <div className="nx-login__code">
        <span className="nx-lbl">код для бота</span>
        <b>{waiting.code}</b>
        {/* Остаток — рядом с кодом, а не в конце абзаца: это единственное, что
            в этой плитке меняется, и оно отвечает на вопрос «я ещё успеваю». */}
        <span className="nx-login__left">{clock(left)}</span>
      </div>
      <a className="nx-login__cta" href={waiting.link} target="_blank" rel="noreferrer">
        Открыть Telegram
      </a>
      <p className="nx-login__hint">
        Нажмите в боте <b>«Начать»</b> — эта вкладка откроет сканер сама, возвращаться сюда не
        нужно. Код вводить руками не требуется: он уже в ссылке.
      </p>
      <button
        type="button"
        className="nx-login__back"
        onClick={() => useSession.getState().cancel()}
      >
        отменить
      </button>
    </div>
  );
}
